import React from 'react';
import { jsPDF } from 'jspdf';
import { Box } from '@mui/material';
import { useText } from '../context/TextContext';

const COLORS = {
  text: [36, 36, 36],
  muted: [96, 94, 92],
  line: [237, 235, 233],
  accent: [0, 120, 212],
};

const generateATSResume = (textData) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const margin = 16;
  const topMargin = 16;
  const bottomMargin = 16;
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const contentWidth = pageWidth - 2 * margin;
  const lineHeight = 5.2;
  const columnGap = 8;
  const columnWidth = (contentWidth - columnGap) / 2;
  let yPos = topMargin;

  const setText = () => doc.setTextColor(...COLORS.text);
  const setMuted = () => doc.setTextColor(...COLORS.muted);
  const setAccent = () => doc.setTextColor(...COLORS.accent);

  const ensureSpace = (needed) => {
    if (yPos + needed > pageHeight - bottomMargin) {
      doc.addPage();
      yPos = topMargin;
      return true;
    }
    return false;
  };

  const addHorizontalRule = () => {
    ensureSpace(6);
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
  };

  const writeWrapped = (text, options = {}) => {
    const {
      size = 10,
      style = 'normal',
      color = 'text',
      maxWidth = contentWidth,
      x = margin,
      spacing = lineHeight,
    } = options;

    if (color === 'muted') setMuted();
    else if (color === 'accent') setAccent();
    else setText();

    doc.setFont('helvetica', style);
    doc.setFontSize(size);

    const lines = doc.splitTextToSize(String(text || ''), maxWidth);
    lines.forEach((line) => {
      ensureSpace(spacing + 1);
      doc.text(line, x, yPos);
      yPos += spacing;
    });

    return lines.length;
  };

  const writeSectionTitle = (title) => {
    yPos += 1;
    writeWrapped(title, { size: 12, style: 'bold', color: 'accent', spacing: 6 });
    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(0.4);
    doc.line(margin, yPos - 2, margin + 18, yPos - 2);
    yPos += 2;
  };

  // Keep section title + list together; fix page-break gap from stale Y coordinates
  const writeSectionList = (title, items) => {
    if (!items || items.length === 0) return;

    const left = items.slice(0, Math.ceil(items.length / 2));
    const right = items.slice(Math.ceil(items.length / 2));
    const rows = Math.max(left.length, right.length);
    const rowHeight = 4.8;
    const titleBlock = 12;
    const listBlock = rows * rowHeight + 2;

    // If title + list won't fit, start both on the next page (avoids orphan title + huge gap)
    ensureSpace(titleBlock + Math.min(listBlock, 40));

    writeSectionTitle(title);

    // If the remaining list still doesn't fit after the title, move list to next page
    if (yPos + listBlock > pageHeight - bottomMargin) {
      doc.addPage();
      yPos = topMargin;
    }

    const startY = yPos;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setText();

    for (let index = 0; index < rows; index += 1) {
      const y = startY + index * rowHeight;
      if (left[index]) {
        doc.text(`• ${left[index]}`, margin, y, { maxWidth: columnWidth });
      }
      if (right[index]) {
        doc.text(`• ${right[index]}`, margin + columnWidth + columnGap, y, {
          maxWidth: columnWidth,
        });
      }
    }

    yPos = startY + listBlock + 2;
  };

  const calculateYearsOfExperience = () => {
    if (!textData.experiences || textData.experiences.length === 0) return '0';
    try {
      let firstJobExperience = null;
      for (let i = textData.experiences.length - 1; i >= 0; i--) {
        const exp = textData.experiences[i];
        if (exp?.duration) {
          const startPart = exp.duration.split(/[–-]/)[0].trim();
          if (startPart.includes('/') && startPart.split('/').length === 2) {
            firstJobExperience = exp;
            break;
          }
        }
      }
      if (!firstJobExperience?.duration) {
        firstJobExperience = textData.experiences[textData.experiences.length - 1];
      }
      if (!firstJobExperience?.duration) return '0';

      const startDateStr = firstJobExperience.duration.split(/[–-]/)[0].trim();
      let month;
      let year;
      if (startDateStr.includes('/')) {
        const parts = startDateStr.split('/');
        if (parts.length !== 2) return '0';
        month = parseInt(parts[0], 10);
        year = parseInt(parts[1], 10);
      } else {
        year = parseInt(startDateStr, 10);
        month = 1;
      }
      if (isNaN(year) || (month && isNaN(month))) return '0';

      const startDate = new Date(year, (month || 1) - 1, 1);
      const now = new Date();
      const yearsDiff = now.getFullYear() - startDate.getFullYear();
      const monthsDiff = now.getMonth() - startDate.getMonth();
      const totalYears = monthsDiff < 0 ? yearsDiff - 1 : yearsDiff;
      const totalMonths = monthsDiff < 0 ? 12 + monthsDiff : monthsDiff;
      return totalMonths >= 6 ? `${totalYears + 1}+` : `${totalYears}+`;
    } catch {
      return '0';
    }
  };

  const yearsOfExperience = calculateYearsOfExperience();
  const info = textData.personalInfo || {};
  const roles = textData.commercialRoles || [];
  const openTo = textData.openTo || '';

  // Header
  writeWrapped(info.name || '', { size: 18, style: 'bold', spacing: 7 });
  writeWrapped(info.position || '', { size: 11, style: 'normal', color: 'muted', spacing: 5.5 });
  writeWrapped(`${yearsOfExperience} years of experience`, { size: 9.5, color: 'muted', spacing: 5 });
  yPos += 1;

  const contactLine = [
    info.email,
    info.location,
    info.website || 'https://mariob.cloud',
    textData.products?.url || 'https://mariob.cloud/products',
  ].filter(Boolean).join('  ·  ');
  writeWrapped(contactLine, { size: 8.5, color: 'muted', spacing: 4.5 });
  if (info.linkedin) {
    writeWrapped(info.linkedin, { size: 8.5, color: 'muted', spacing: 4.5 });
  }
  yPos += 1;
  addHorizontalRule();

  if (roles.length > 0) {
    ensureSpace(18);
    writeSectionTitle('Commercial Roles');
    writeWrapped(roles.join('  ·  '), { size: 9.5, spacing: 5 });
    yPos += 1;
  }

  if (openTo) {
    ensureSpace(22);
    writeSectionTitle('Open To');
    writeWrapped(openTo, { size: 9.5, spacing: 5 });
    yPos += 1;
  }

  ensureSpace(28);
  writeSectionTitle('About');
  writeWrapped(textData.aboutMe || '', { size: 9.5, spacing: 5 });
  yPos += 1;

  // Bold keyword block for ATS / AI CV screeners (high-signal terms)
  if (textData.priorityKeywords?.length) {
    ensureSpace(22);
    writeSectionTitle('Core Keywords');
    writeWrapped(textData.priorityKeywords.join('  |  '), {
      size: 10,
      style: 'bold',
      spacing: 5.2,
    });
    yPos += 1;
  }

  if (textData.skills?.main?.length) {
    writeSectionList('Main Skills', textData.skills.main);
  }
  if (textData.skills?.other?.length) {
    writeSectionList('Other Skills', textData.skills.other);
  }
  if (textData.skills?.integrations?.length) {
    writeSectionList('Integrations', textData.skills.integrations);
  }
  if (textData.skills?.aiTools?.length) {
    writeSectionList('AI Tools', textData.skills.aiTools);
  }

  // Products portfolio — expands what recruiters/AI see beyond employment history
  if (textData.products?.items?.length) {
    ensureSpace(30);
    writeSectionTitle('Products');
    writeWrapped(textData.products.url || 'https://mariob.cloud/products', {
      size: 9,
      color: 'accent',
      spacing: 5,
    });
    writeWrapped(
      'Selected products I built and ship (full details at the URL above):',
      { size: 9, color: 'muted', spacing: 4.8 }
    );
    textData.products.items.forEach((product) => {
      ensureSpace(16);
      writeWrapped(product.name || '', { size: 10, style: 'bold', spacing: 5 });
      if (product.summary) {
        writeWrapped(product.summary, { size: 9.5, spacing: 4.8 });
      }
      if (product.url) {
        writeWrapped(product.url, { size: 8.5, color: 'muted', spacing: 4.5 });
      }
      yPos += 1.5;
    });
  }

  if (textData.experiences?.length) {
    ensureSpace(30);
    writeSectionTitle('Experience');
    textData.experiences.forEach((exp) => {
      const bulletCount = (exp.projects || []).length;
      ensureSpace(18 + bulletCount * 5);
      writeWrapped(exp.title || '', { size: 10.5, style: 'bold', spacing: 5 });
      writeWrapped(`${exp.company || ''}  ·  ${exp.duration || ''}`, {
        size: 9,
        color: 'muted',
        spacing: 4.8,
      });
      if (exp.companyUrl) {
        writeWrapped(exp.companyUrl, { size: 8.5, color: 'muted', spacing: 4.5 });
      }
      (exp.projects || []).forEach((project) => {
        writeWrapped(`• ${project}`, { size: 9.5, spacing: 4.8 });
      });
      if (exp.skills?.length) {
        writeWrapped(`Skills: ${exp.skills.join(', ')}`, {
          size: 8.5,
          color: 'muted',
          spacing: 4.5,
        });
      }
      yPos += 2.5;
    });
  }

  if (textData.languages?.length) {
    writeSectionList(
      'Languages',
      textData.languages.map((item) => item.lang)
    );
  }

  if (textData.education) {
    ensureSpace(24);
    writeSectionTitle('Education');
    writeWrapped(textData.education.degree || '', { size: 10, style: 'bold', spacing: 5 });
    writeWrapped(textData.education.institution || '', { size: 9.5, spacing: 4.8 });
    writeWrapped(textData.education.duration || '', { size: 9, color: 'muted', spacing: 4.8 });
    yPos += 2;
  }

  if (textData.certifications?.length) {
    ensureSpace(28);
    writeSectionTitle('Certifications');
    textData.certifications.forEach((cert) => {
      ensureSpace(20);
      writeWrapped(cert.title || '', { size: 10, style: 'bold', spacing: 5 });
      writeWrapped(
        `${cert.issuer || ''}  ·  Issued ${cert.issued || ''}  ·  Expired ${cert.expired || ''}`,
        { size: 9, color: 'muted', spacing: 4.6 }
      );
      if (cert.credentialId) {
        writeWrapped(`Credential ID: ${cert.credentialId}`, {
          size: 8.5,
          color: 'muted',
          spacing: 4.4,
        });
      }
      if (cert.link) {
        writeWrapped(cert.link, { size: 8.5, color: 'muted', spacing: 4.4 });
      }
      yPos += 2;
    });
  }

  doc.save('Mario_Bozhinovski_CV.pdf');
};

const DownloadButton = () => {
  const textData = useText();

  const handleDownload = () => {
    if (textData) {
      generateATSResume(textData);
    }
  };

  return (
    <Box
      component="a"
      data-download-cv
      onClick={(e) => {
        e.preventDefault();
        handleDownload();
      }}
      sx={{
        fontFamily: 'inherit',
        fontSize: '0.875rem',
        color: '#FFFFFF',
        textDecoration: 'none',
        cursor: 'pointer',
        padding: '8px 16px',
        border: '1px solid #0078D4',
        borderRadius: '2px',
        backgroundColor: '#0078D4',
        transition: 'background-color 0.15s ease',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: '#106EBE',
          borderColor: '#106EBE',
        }
      }}
    >
      Download CV
    </Box>
  );
};

export default DownloadButton;
